import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import {
  Transfer,
  CreateTransferDto,
  TransferConfirmation,
  TransferTracking,
  ApiResponse,
  PaginatedResponse,
  ReasonCode,
  TransferStatus
} from '../models';

/**
 * TransferService - API Service Layer
 * 
 * This service handles all API calls for transfer operations.
 * Currently using mock data - replace with HttpClient calls when backend is ready.
 * 
 * Responsibilities:
 * - API communication only
 * - Observable-based async operations
 * - Error handling
 * - Data transformation (if needed)
 */
@Injectable({
  providedIn: 'root'
})
export class TransferService {
  // ==========================================
  // MOCK DATA - Remove when connecting to real API
  // ==========================================
  private pendingTransfersDb: Transfer[] = [];
  private trackingDb: TransferTracking[] = [];
  private nextId = 1;

  // Observable streams (single source of truth)
  private pendingTransfersSubject = new BehaviorSubject<Transfer[]>([]);
  private trackingSubject = new BehaviorSubject<TransferTracking[]>([]);

  // Public observables for components to subscribe to
  public pendingTransfers$ = this.pendingTransfersSubject.asObservable();
  public tracking$ = this.trackingSubject.asObservable();

  // Mock reason codes (would come from backend config)
  private reasonCodes: ReasonCode[] = [
    { code: 'PROCUREMENT', description: 'Procurement' },
    { code: 'MAINTENANCE', description: 'Maintenance' },
    { code: 'REPAIR_COMPLETE', description: 'Repair Complete' },
    { code: 'QUALITY_CONTROL', description: 'Quality Control' },
    { code: 'PROJECT_RETURN', description: 'Project Return' },
    { code: 'DAMAGE_ASSESSMENT', description: 'Damage Assessment' },
    { code: 'INVENTORY_AUDIT', description: 'Inventory Audit' },
    { code: 'OTHER', description: 'Other (Specify in Details)' }
  ];

  constructor() {
    // Initialize with empty data
    this.emitUpdates();
  }

  /**
   * Get available reason codes
   * Future: GET /api/transfers/reason-codes
   */
  getReasonCodes(): Observable<ApiResponse<ReasonCode[]>> {
    return of({
      success: true,
      data: this.reasonCodes
    }).pipe(delay(100)); // Simulate network delay
  }

  /**
   * Get all pending transfers
   * Future: GET /api/transfers?status=pending
   */
  getPendingTransfers(): Observable<PaginatedResponse<Transfer>> {
    return of({
      data: [...this.pendingTransfersDb],
      total: this.pendingTransfersDb.length,
      page: 1,
      pageSize: 50
    }).pipe(delay(300)); // Simulate network delay
  }

  /**
   * Create a new transfer
   * Future: POST /api/transfers
   */
  createTransfer(dto: CreateTransferDto): Observable<ApiResponse<Transfer>> {
    // Validation
    if (!dto.fromHub || !dto.toHub || dto.qty <= 0) {
      return throwError(() => ({
        success: false,
        message: 'Invalid transfer data',
        errors: ['All fields are required and quantity must be greater than 0']
      })).pipe(delay(100));
    }

    if (dto.fromHub === dto.toHub) {
      return throwError(() => ({
        success: false,
        message: 'Invalid transfer',
        errors: ['Source and destination hubs cannot be the same']
      })).pipe(delay(100));
    }

    // Create new transfer
    const transfer: Transfer = {
      id: this.generateId(),
      fromHub: dto.fromHub,
      toHub: dto.toHub,
      matType: dto.matType,
      qty: dto.qty,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add to mock database
    this.pendingTransfersDb.push(transfer);
    this.emitUpdates();

    return of({
      success: true,
      data: transfer,
      message: 'Transfer created successfully'
    }).pipe(delay(200));
  }

  /**
   * Create multiple transfers at once
   * Future: POST /api/transfers/batch
   */
  createTransfers(dtos: CreateTransferDto[]): Observable<ApiResponse<Transfer[]>> {
    if (!dtos || dtos.length === 0) {
      return throwError(() => ({
        success: false,
        message: 'No transfers provided',
        errors: ['At least one transfer is required']
      }));
    }

    const transfers: Transfer[] = dtos.map(dto => ({
      id: this.generateId(),
      fromHub: dto.fromHub,
      toHub: dto.toHub,
      matType: dto.matType,
      qty: dto.qty,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date()
    }));

    // Add to mock database
    this.pendingTransfersDb.push(...transfers);
    this.emitUpdates();

    return of({
      success: true,
      data: transfers,
      message: `${transfers.length} transfer(s) created successfully`
    }).pipe(delay(300));
  }

  /**
   * Confirm transfers with reason
   * Future: POST /api/transfers/confirm
   */
  confirmTransfers(transferIds: string[], reasonCode: string, reasonDetails?: string): Observable<ApiResponse<TransferTracking[]>> {
    // Validation
    if (!transferIds || transferIds.length === 0) {
      return throwError(() => ({
        success: false,
        message: 'No transfers selected',
        errors: ['At least one transfer must be selected for confirmation']
      })).pipe(delay(100));
    }

    if (!reasonCode) {
      return throwError(() => ({
        success: false,
        message: 'Reason code required',
        errors: ['A reason code must be provided']
      })).pipe(delay(100));
    }

    // Find transfers to confirm
    const transfersToConfirm = this.pendingTransfersDb.filter(t => transferIds.includes(t.id));

    if (transfersToConfirm.length === 0) {
      return throwError(() => ({
        success: false,
        message: 'No matching transfers found',
        errors: ['The specified transfers could not be found']
      })).pipe(delay(100));
    }

    // Create confirmation object
    const confirmation: TransferConfirmation = {
      transferIds,
      reasonCode,
      reasonDetails,
      confirmedAt: new Date(),
      confirmedBy: 'current-user' // In real app, get from auth service
    };

    // Create tracking records
    const trackingRecords: TransferTracking[] = transfersToConfirm.map(transfer => {
      // Update transfer status
      transfer.status = 'confirmed';
      transfer.updatedAt = new Date();

      return {
        id: this.generateId(),
        transfer: { ...transfer },
        reasonCode: reasonCode,
        reasonDetails: reasonDetails,
        confirmedBy: 'current-user', // In real app, get from auth service
        status: 'confirmed',
        estimatedArrival: this.calculateEstimatedArrival(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
    });

    // Remove from pending transfers
    this.pendingTransfersDb = this.pendingTransfersDb.filter(
      t => !transferIds.includes(t.id)
    );

    // Add to tracking
    this.trackingDb.push(...trackingRecords);
    this.emitUpdates();

    return of({
      success: true,
      data: trackingRecords,
      message: `${trackingRecords.length} transfer(s) confirmed successfully`
    }).pipe(delay(400));
  }

  /**
   * Get transfer tracking records
   * Future: GET /api/transfers/tracking
   */
  getTransferTracking(status?: TransferStatus): Observable<PaginatedResponse<TransferTracking>> {
    let filteredTracking = [...this.trackingDb];

    if (status) {
      filteredTracking = filteredTracking.filter(t => t.status === status);
    }

    // Sort by most recent first
    filteredTracking.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    return of({
      data: filteredTracking,
      total: filteredTracking.length,
      page: 1,
      pageSize: 50
    }).pipe(delay(300));
  }

  /**
   * Delete a pending transfer
   * Future: DELETE /api/transfers/:id
   */
  deletePendingTransfer(transferId: string): Observable<ApiResponse<void>> {
    const index = this.pendingTransfersDb.findIndex(t => t.id === transferId);

    if (index === -1) {
      return throwError(() => ({
        success: false,
        message: 'Transfer not found',
        errors: ['The specified transfer could not be found']
      })).pipe(delay(100));
    }

    this.pendingTransfersDb.splice(index, 1);
    this.emitUpdates();

    return of({
      success: true,
      message: 'Transfer deleted successfully'
    }).pipe(delay(200));
  }

  /**
   * Update transfer tracking status (simulate transit/completion)
   * Future: PATCH /api/transfers/tracking/:id/status
   */
  updateTrackingStatus(trackingId: string, status: TransferStatus): Observable<ApiResponse<TransferTracking>> {
    const tracking = this.trackingDb.find(t => t.id === trackingId);

    if (!tracking) {
      return throwError(() => ({
        success: false,
        message: 'Tracking record not found'
      }));
    }

    tracking.status = status;
    tracking.updatedAt = new Date();
    tracking.transfer.status = status;

    if (status === 'completed') {
      tracking.actualArrival = new Date();
    }

    this.emitUpdates();

    return of({
      success: true,
      data: tracking,
      message: 'Status updated successfully'
    }).pipe(delay(200));
  }

  /**
   * Clear all data (for testing/demo purposes)
   */
  clearAllData(): void {
    this.pendingTransfersDb = [];
    this.trackingDb = [];
    this.emitUpdates();
  }

  // ===== Private Helper Methods =====

  private generateId(): string {
    return `TRF-${Date.now()}-${this.nextId++}`;
  }

  private calculateEstimatedArrival(): Date {
    const estimatedDate = new Date();
    estimatedDate.setDate(estimatedDate.getDate() + 2); // 2 days from now
    return estimatedDate;
  }

  private emitUpdates(): void {
    this.pendingTransfersSubject.next([...this.pendingTransfersDb]);
    this.trackingSubject.next([...this.trackingDb]);
  }
}
